import React, { FunctionComponent, useCallback, useState } from 'react';
import {
  AdminDistributorProductDistributionInformation,
  AdminProductDistributionPlatformListing,
  DistributionPlatform,
} from 'src/api';
import DocumentModal from 'src/libraries/document-modal';
import Button from 'src/ui/atoms/button';
import CardItem from 'src/ui/atoms/card-item';
import Date from 'src/ui/atoms/date';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import Spacer from 'src/ui/atoms/spacer';
import Translate from 'src/ui/atoms/translate';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import ActionButtons from 'src/ui/molecules/action-buttons';
import BoxedContent from 'src/ui/molecules/boxed-content';
import EditDistributionInformation from '../edit-distribution-information';

export type ExtendedDistributionInformation = AdminDistributorProductDistributionInformation & {
  platformId: string;
  platformName: string;
  productDistributionPlatformListingId: string;
};

interface ListDistributionInformationsProps {
  distributionInformations: ExtendedDistributionInformation[];
  hasEditDistributionInformationPermission: boolean;
  onDistributionInformationAdded: () => void;
  listings: AdminProductDistributionPlatformListing[];
  distributionPlatformsWithEditPermission: DistributionPlatform[];
}

const ListDistributionInformations: FunctionComponent<ListDistributionInformationsProps> = ({
  distributionInformations,
  hasEditDistributionInformationPermission,
  onDistributionInformationAdded,
  listings,
  distributionPlatformsWithEditPermission,
}) => {
  const translate = useTranslateWithStringArgs();

  const [isEditDistributionInformationOpen, setEditDistributionInformationOpen] = useState(false);
  const [dataToEdit, setDataToEdit] = useState<ExtendedDistributionInformation | null>(null);

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return '-';

    return value;
  };

  const getDistributionInfo = useCallback(
    (info: ExtendedDistributionInformation) => {
      const result: any = {
        distributionPlatform: info.platformName,
        complianceProductName: info.complianceProductName,
        useUnits: info.useUnits
          ? translate('distributionInformation.fields.useUnits.units')
          : translate('distributionInformation.fields.useUnits.amount'),
        unitSteps: info.unitSteps,
        selfDisclosureRequired: translate(`commonOptions.${info.selfDisclosureRequired}`),
        investmentExperienceMinScore: info.investmentExperienceMinScore
          ? info.investmentExperienceMinScore.toString()
          : null,
        serviceExperienceMinScore: info.serviceExperienceMinScore ? info.serviceExperienceMinScore.toString() : null,
        capitalType: info.costsvariables?.capitalType
          ? translate(`distributionInformation.fields.capitalType.${info.costsvariables.capitalType.toLowerCase()}`)
          : null,
      };

      if (info.costsvariables?.productStartDate && info.costsvariables?.productEndDate) {
        result.productDate = (
          <>
            <Date>{info.costsvariables?.productStartDate}</Date> - <Date>{info.costsvariables?.productEndDate}</Date>
          </>
        );
      } else {
        result.runtimeYears = info.costsvariables?.runtimeYears?.toString();
      }

      result.costsBrokerageNominalAmountPercent = info.costsvariables?.costsBrokerageNominalAmountPercent
        ? `${info.costsvariables.costsBrokerageNominalAmountPercent}%`
        : null;
      result.grantsNominalAmountPercent = info.costsvariables?.grantsNominalAmountPercent
        ? `${info.costsvariables.grantsNominalAmountPercent}%`
        : null;
      result.costsBrokerageNavPercent = info.costsvariables?.costsBrokerageNavPercent
        ? `${info.costsvariables.costsBrokerageNavPercent}%`
        : null;
      result.grantsNavPercent = info.costsvariables?.grantsNavPercent
        ? `${info.costsvariables.grantsNavPercent}%`
        : null;
      result.costsTransactionDepotNominalAmountPercent = info.costsvariables?.costsTransactionDepotNominalAmountPercent
        ? `${info.costsvariables.costsTransactionDepotNominalAmountPercent}%`
        : null;
      result.costsTransactionDepotNavPercent = info.costsvariables?.costsTransactionDepotNavPercent
        ? `${info.costsvariables.costsTransactionDepotNavPercent}%`
        : null;
      result.productImage = info.productImage ? (
        <Translate
          name="common.viewImage"
          args={[
            (text: string, key) => {
              return (
                <DocumentModal key={key} fileType="image" {...info.productImage}>
                  {text}
                </DocumentModal>
              );
            },
          ]}
        />
      ) : null;

      return result;
    },
    [translate],
  );

  return (
    <>
      <Spacer y={2} />
      {distributionInformations.map((info, index) => (
        <React.Fragment key={info.id || index}>
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true}>
            {Object.entries(getDistributionInfo(info)).map(([key, value], entryIndex) => (
              <CardItem
                title={<Translate name={`distributionInformationDetails.fields.${key}.label`} />}
                content={<PlaceholderFallback>{formatValue(value)}</PlaceholderFallback>}
                key={entryIndex}
              />
            ))}
            {hasEditDistributionInformationPermission && (
              <ActionButtons spacingTop="regular">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDataToEdit(info);
                    setEditDistributionInformationOpen(true);
                  }}
                >
                  <Translate name="common.edit" />
                </Button>
              </ActionButtons>
            )}
          </BoxedContent>
        </React.Fragment>
      ))}
      {isEditDistributionInformationOpen && dataToEdit && (
        <EditDistributionInformation
          onClose={() => setEditDistributionInformationOpen(false)}
          onDistributionInformationAdded={() => {
            setEditDistributionInformationOpen(false);
            onDistributionInformationAdded();
          }}
          listings={listings}
          distributionPlatformsWithEditPermission={distributionPlatformsWithEditPermission}
          data={dataToEdit}
        ></EditDistributionInformation>
      )}
    </>
  );
};

export default ListDistributionInformations;
